import React, { useEffect, useState } from "react";
import { useWeddingData } from "../hooks/useWeddingData";
import FloatingHearts from "./FloatingHearts";
import "./HeroSection.css";

const HeroSection: React.FC = () => {
  const { weddingData, loading } = useWeddingData();
  const [randomEmojis, setRandomEmojis] = useState({
    groom: "🤵‍♂️",
    heart: "💕",
    bride: "👰‍♀️",
  });

  // 기기 가로/세로 상태
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // 랜덤 별 위치 생성 함수
  const generateRandomStars = (count: number) => {
    return Array.from({ length: count }, (_, index) => ({
      id: index,
      left: Math.random() * 200 - 100, // -100px ~ 100px
      top: Math.random() * 100 - 50, // -50px ~ 50px
      delay: Math.random() * 2, // 0~2초 딜레이
      size: 0.6 + Math.random() * 0.4, // 0.6~1.0 크기
      duration: 1.5 + Math.random() * 1, // 1.5~2.5초 애니메이션
    }));
  };

  const [groomStars, setGroomStars] = useState(generateRandomStars(4));
  const [brideStars, setBrideStars] = useState(generateRandomStars(4));

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 랜덤 이모지 배열
  const groomEmojis = ["🤵‍♂️"];
  const heartEmojis = ["💕", "💖", "💗", "💘", "💝", "💞", "💟", "❤️"];
  const brideEmojis = ["👰‍♀️"];

  useEffect(() => {
    // 페이지 로드 시 랜덤 이모지 설정
    const getRandomEmoji = (emojiArray: string[]) => {
      return emojiArray[Math.floor(Math.random() * emojiArray.length)];
    };

    // 신랑복과 웨딩드레스로 고정
    setRandomEmojis({
      groom: "🤵‍♂️",
      heart: getRandomEmoji(heartEmojis),
      bride: "👰‍♀️",
    });

    // 별 위치도 랜덤하게 재생성
    setGroomStars(generateRandomStars(4));
    setBrideStars(generateRandomStars(4));
  }, []);

  const formatWeddingDate = (dateString: string, timeString: string) => {
    if (!dateString || !timeString) {
      console.log("Date or time is missing:", { dateString, timeString });
      return { date: "2024년 12월 31일 (화)", time: "오후 2시 30분" };
    }

    try {
      // 날짜 형식 처리 (YYYY-MM-DD 또는 다른 형식)
      let year, month, day;

      if (dateString.includes("-")) {
        [year, month, day] = dateString.split("-").map(Number);
      } else if (dateString.includes("/")) {
        [year, month, day] = dateString.split("/").map(Number);
      } else {
        // 다른 형식의 날짜 처리
        const date = new Date(dateString);
        year = date.getFullYear();
        month = date.getMonth() + 1;
        day = date.getDate();
      }

      // 요일 계산
      const dateObj = new Date(year, month - 1, day);
      const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
      const weekday = weekdays[dateObj.getDay()];

      // 시간 형식 처리 (HH:MM 또는 다른 형식)
      let hours, minutes;

      if (timeString.includes(":")) {
        [hours, minutes] = timeString.split(":").map(Number);
      } else {
        // 시간이 숫자로만 되어있는 경우
        hours = parseInt(timeString);
        minutes = 0;
      }

      const ampm = hours >= 12 ? "오후" : "오전";
      const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;

      console.log("Formatted date:", {
        year,
        month,
        day,
        hours,
        minutes,
        ampm,
        displayHours,
        weekday,
      });

      return {
        date: `${year}년 ${month}월 ${day}일 (${weekday})`,
        time: `${ampm} ${displayHours}시 ${minutes}분`,
      };
    } catch (error) {
      console.error("Error formatting date:", error, {
        dateString,
        timeString,
      });
      return { date: "2024년 12월 31일 (화)", time: "오후 2시 30분" };
    }
  };

  // 디데이 계산 함수
  const calculateDday = (dateString: string) => {
    if (!dateString) return 0;

    try {
      let year, month, day;

      if (dateString.includes("-")) {
        [year, month, day] = dateString.split("-").map(Number);
      } else if (dateString.includes("/")) {
        [year, month, day] = dateString.split("/").map(Number);
      } else {
        const date = new Date(dateString);
        year = date.getFullYear();
        month = date.getMonth() + 1;
        day = date.getDate();
      }

      const weddingDate = new Date(year, month - 1, day);
      const today = new Date();

      // 시간을 제거하고 날짜만 비교
      weddingDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      const diffTime = weddingDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return diffDays;
    } catch (error) {
      console.error("Error calculating D-day:", error);
      return 0;
    }
  };

  const dday = calculateDday(weddingData?.date || "");

  return (
    <div className="hero-section">
      <FloatingHearts />

      {/* Hero Section */}
      <div className="parallax-container">
        <div className="parallax-content">
          <h1 className="parallax-title">
            Wedding
            <br />
            Invitation
          </h1>
          <p className="parallax-subtitle">
            {weddingData?.parallaxSubtitle || "Loading..."}
          </p>
          <div className="couple-names">
            <span className="groom-emoji">{randomEmojis.groom}</span>
            <span className="groom-name">
              {weddingData?.groom?.name || "신랑"}
              {groomStars.map((star) => (
                <span
                  key={star.id}
                  className="random-star"
                  style={{
                    left: `${star.left}px`,
                    top: `${star.top}px`,
                    fontSize: `${star.size}rem`,
                    animationDelay: `${star.delay}s`,
                    animationDuration: `${star.duration}s`,
                  }}
                >
                  ✨
                </span>
              ))}
            </span>
            <span className="heart-emoji">{randomEmojis.heart}</span>
            <span className="bride-name">
              {weddingData?.bride?.name || "신부"}
              {brideStars.map((star) => (
                <span
                  key={star.id}
                  className="random-star"
                  style={{
                    left: `${star.left}px`,
                    top: `${star.top}px`,
                    fontSize: `${star.size}rem`,
                    animationDelay: `${star.delay}s`,
                    animationDuration: `${star.duration}s`,
                  }}
                >
                  ✨
                </span>
              ))}
            </span>
            <span className="bride-emoji">{randomEmojis.bride}</span>
          </div>
          {/* 날짜/시간 제거, venue 정보 추가 */}
          {loading ? (
            <div>로딩 중...</div>
          ) : (
            <div className="venue">
              <p className="date">
                {formatWeddingDate(weddingData?.date || "", weddingData?.time || "").date}
              </p>
              <p className="time">
                {formatWeddingDate(weddingData?.date || "", weddingData?.time || "").time}
              </p>
              <p className="venue-name">
                {weddingData?.venue?.name || "결혼식장"}
              </p>
            </div>
          )}
          <div className="parallax-dday">
            {loading
              ? "로딩 중..."
              : dday > 0
              ? `D-${dday}`
              : dday === 0
              ? "D-Day"
              : `D+${Math.abs(dday)}`}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
